import _ from "lodash";
import React, { useCallback, useState } from "react";
import { connect } from "react-redux";
import classNames from "classnames";
import { IconButton } from "@toddle-edu/ds-web";
import { useI18n } from "Hooks";
import { useDispatch } from "Hooks/useDispatch";
import { SearchOutlined, CloseOutlined } from "@toddle-edu/ds-icons";

import { useDebouncedCallback } from "Utils/CustomHooks";

import {
  getMessagingV2ReduxState,
  setRevampV2FilterSearch,
  setRevampV2SearchFocus,
} from "MessagingV2/modules/MessagingModule";
import { isMessagingV2ChannelEnabled } from "MessagingV2/modules/Utils";

import classes from "./ThreadV2SearchFilter.scss";

export const CustomSearchInput = React.forwardRef((props, ref) => {
  const {
    placeholder,
    value,
    onChange,
    onCloseClick,
    isSearchFocused,
    testId,
    ...rest
  } = props;

  const [searchValue, setSearchValue] = useState(value);
  const [handleValueChange] = useDebouncedCallback(onChange, 300);

  const onValueChange = e => {
    const value = e?.target?.value || "";
    setSearchValue(value);
    handleValueChange(value);
  };

  const handleCloseClick = useCallback(() => {
    setSearchValue("");
    onCloseClick();
  }, [onCloseClick]);

  const isValuePresent = Boolean(value) || isSearchFocused;
  return (
    <div
      data-ds-version="2.0"
      className={classNames(classes.customSearchInputContainer, {
        [classes.customSearchInputContainerWithFocus]: isValuePresent,
      })}
    >
      <SearchOutlined className={classes.customSearchIcon} />
      <input
        ref={ref}
        dir="auto"
        type="text"
        className={classNames("text-body", classes.customSearchInput, {
          [classes.customSearchInputWithValues]: isSearchFocused,
        })}
        value={searchValue}
        onChange={onValueChange}
        placeholder={placeholder}
        data-test-id={`${testId}-custom-search-input`}
        {...rest}
      />
      {isValuePresent ? (
        <IconButton
          onClick={handleCloseClick}
          variant="neutral"
          size="small"
          type="plain"
          dsVersion="2.0"
          icon={<CloseOutlined />}
          testId={`${testId}-clear`}
        />
      ) : null}
    </div>
  );
});

CustomSearchInput.displayName = "CustomSearchInput";

const ThreadV2SearchFIlter = props => {
  const { searchFilter, isSearchFocused, testId } = props;

  const ref = React.useRef(null);

  React.useEffect(() => {
    if (isSearchFocused && ref.current) {
      ref.current.focus();
    }
  }, [isSearchFocused]);

  const { t } = useI18n(["common"]);
  const dispatch = useDispatch();

  const setSearchFilter = useCallback(
    searchText => dispatch(setRevampV2FilterSearch(searchText)),
    [dispatch]
  );

  const onSearchFocus = useCallback(() => {
    dispatch(setRevampV2SearchFocus(true));
  }, [dispatch]);

  const onSearchBlur = useCallback(() => {
    dispatch(setRevampV2SearchFocus(false));
  }, [dispatch]);

  const handleCloseClick = useCallback(() => {
    setSearchFilter("");
    onSearchBlur();
    if (ref.current) {
      ref.current.blur();
    }
  }, [setSearchFilter, onSearchBlur]);

  const isChannelEnabled = isMessagingV2ChannelEnabled();

  return (
    <CustomSearchInput
      ref={ref}
      onFocus={onSearchFocus}
      onBlur={onSearchBlur}
      value={searchFilter}
      placeholder={
        isChannelEnabled
          ? t("common:search_for_chat_or_channel")
          : t("common:search_for_label_lowercase", {
              label: t("common:chat"),
            })
      }
      dsVersion="2.0"
      onCloseClick={handleCloseClick}
      isSearchFocused={isSearchFocused}
      onChange={setSearchFilter}
      testId={testId}
    />
  );
};

const mapStateToProps = state => {
  const messagingState = getMessagingV2ReduxState(state);

  const { searchFilter, isSearchFocused } = _.get(
    messagingState,
    "messageRevampV2FilterState",
    {}
  );

  return {
    searchFilter,
    isSearchFocused,
  };
};
export default connect(mapStateToProps)(ThreadV2SearchFIlter);
